import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  getTemplates,
  createTemplate,
  deleteTemplate
} from "../api/api";
import { useLoading } from "./useLoading";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFromCampaignModal, setShowFromCampaignModal] = useState(false);

  const [newTemplate, setNewTemplate] = useState({
    name: "",
    subject: "",
    body: ""
  });

  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [copiedText, setCopiedText] = useState(null);

  const { loading, withLoading } = useLoading();

  useEffect(() => {
    loadTemplates();
    loadCampaigns();
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await withLoading(() => getTemplates());
      setTemplates(res.data);
    } catch (err) {
      console.error("Ошибка загрузки шаблонов:", err);
    }
  };

  const loadCampaigns = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/campaigns`);
      setCampaigns(res.data);
    } catch (err) {
      console.error("Ошибка загрузки кампаний:", err);
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.name.trim()) return alert("Введите название");

    try {
      await withLoading(() => createTemplate(newTemplate));
      setNewTemplate({ name: "", subject: "", body: "" });
      setShowCreateModal(false);
      loadTemplates();
    } catch (err) {
      console.error(err);
      alert("Ошибка при создании");
    }
  };

  const handleCreateFromCampaign = async () => {
    if (!selectedCampaignId) return alert("Выберите кампанию");

    const campaign = campaigns.find(c => c.campaignId === selectedCampaignId);
    if (!campaign) return;

    const name = prompt("Название шаблона:", campaign.name || "Без названия");
    if (!name) return;

    const subject = Array.isArray(campaign.subject)
      ? campaign.subject[0]
      : campaign.subject;

  const body = Array.isArray(campaign.texts)
    ? campaign.texts.join("|")
    : campaign.texts || campaign.text || "";

    try {
      await withLoading(() => createTemplate({
        name,
        subject,
        body,
        source_campaign_id: campaign.campaignId
      }));
      setSelectedCampaignId(null);
      setShowFromCampaignModal(false);
      loadTemplates();
      alert("Шаблон создан из кампании!");
    } catch (err) {
      console.error(err);
      alert("Ошибка: " + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Удалить шаблон?")) return;
    try {
      await withLoading(() => deleteTemplate(id));
      loadTemplates();
    } catch (err) {
      console.error(err);
      alert("Ошибка удаления");
    }
  };

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedText(label);
      setTimeout(() => setCopiedText(null), 1500);
    });
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {loading && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="w-20 h-20 border-8 border-gray-200 border-t-8 border-t-blue-600 rounded-full animate-spin" />
        </div>
      )}

      {copiedText && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 bg-green-600 text-white px-4 py-2 rounded-xl shadow-lg z-50 text-sm">
          📋 Скопировано: {copiedText}
        </div>
      )}

      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4 md:mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold">📝 Шаблоны писем</h1>
          <p className="text-xs md:text-sm text-gray-500 mt-1">
            Готовые тексты для рассылок — тема + тело письма
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-2">
          <button
            onClick={() => setShowFromCampaignModal(true)}
            className="w-full md:w-auto px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition shadow-md text-sm font-medium"
          >
            📥 Взять из кампании
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-full md:w-auto px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition shadow-md text-sm font-medium"
          >
            ➕ Создать шаблон
          </button>
        </div>
      </div>

      {/* LIST */}
      {templates.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-md p-10 text-center">
          <div className="text-4xl mb-3">📝</div>
          <h3 className="text-lg font-semibold mb-2">Пока нет шаблонов</h3>
          <p className="text-sm text-gray-500 mb-4">
            Создайте вручную или возьмите из запущенной кампании
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {templates.map(t => (
            <div
              key={t.id}
              className="bg-white rounded-2xl shadow-md p-4 flex flex-col"
            >
              <div className="flex justify-between items-start gap-2 mb-2">
                <h3 className="font-semibold text-gray-800 truncate flex-1">
                  {t.name}
                </h3>
                <button
                  onClick={() => handleDelete(t.id)}
                  className="text-red-400 hover:text-red-600 text-lg leading-none flex-shrink-0"
                  title="Удалить"
                >
                  ×
                </button>
              </div>

              <div className="text-xs text-gray-400 mb-3">
                {formatDate(t.created_at)}
                {t.source_campaign_id && ` · из кампании #${t.source_campaign_id}`}
              </div>

              {/* Тема */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase font-semibold text-gray-400">
                    Тема
                  </span>
                  {t.subject && (
                    <button
                      onClick={() => copyToClipboard(t.subject, "тема")}
                      className="text-[10px] text-blue-600 hover:underline"
                    >
                      копировать
                    </button>
                  )}
                </div>
                <div
                  className="text-sm text-gray-700 bg-gray-50 rounded-lg p-2 truncate cursor-pointer hover:bg-gray-100"
                  onClick={() => copyToClipboard(t.subject, "тема")}
                  title={t.subject}
                >
                  {t.subject || "(без темы)"}
                </div>
              </div>

              {/* Тело */}
              <div className="flex-1 mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase font-semibold text-gray-400">
                    Текст
                  </span>
                  {t.body && (
                    <button
                      onClick={() => copyToClipboard(t.body, "текст")}
                      className="text-[10px] text-blue-600 hover:underline"
                    >
                      копировать
                    </button>
                  )}
                </div>
                <div
                  className="text-sm text-gray-600 bg-gray-50 rounded-lg p-2 line-clamp-4 cursor-pointer hover:bg-gray-100 whitespace-pre-wrap"
                  onClick={() => copyToClipboard(t.body, "текст")}
                >
                  {t.body || "(пусто)"}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ===== МОДАЛКА: создать вручную ===== */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl p-4 md:p-6 max-w-lg w-full shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg md:text-xl font-semibold mb-4">Новый шаблон</h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Название *
                </label>
                <input
                  type="text"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  placeholder="Например: Приветственное письмо"
                  className="w-full border rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Тема письма
                </label>
                <input
                  type="text"
                  value={newTemplate.subject}
                  onChange={(e) => setNewTemplate({ ...newTemplate, subject: e.target.value })}
                  placeholder="Например: Идея для {{companyName}}"
                  className="w-full border rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Тело письма
                </label>
                <textarea
                  value={newTemplate.body}
                  onChange={(e) => setNewTemplate({ ...newTemplate, body: e.target.value })}
                  rows={8}
                  placeholder={"Добрый день, {{name}}!\n\n..."}
                  className="w-full border rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs">
                <div className="font-medium text-blue-900 mb-2">
                  🔤 Переменные:
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {["{{name}}", "{{companyName}}", "{{email}}", "{{location}}"].map(v => (
                    <code
                      key={v}
                      className="bg-white px-2 py-0.5 rounded border border-blue-200 text-blue-800 font-mono text-[11px] cursor-pointer hover:bg-blue-100"
                      onClick={() => setNewTemplate(prev => ({ ...prev, body: prev.body + v }))}
                    >
                      {v}
                    </code>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:justify-end gap-2 mt-5">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewTemplate({ name: "", subject: "", body: "" });
                }}
                className="px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition order-2 md:order-1"
              >
                Отмена
              </button>
              <button
                onClick={handleCreateTemplate}
                disabled={!newTemplate.name.trim()}
                className={`px-4 py-2 rounded-xl text-white transition order-1 md:order-2 ${
                  newTemplate.name.trim()
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-gray-300 cursor-not-allowed"
                }`}
              >
                Создать
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== МОДАЛКА: взять из кампании ===== */}
      {showFromCampaignModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl p-4 md:p-6 max-w-lg w-full shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg md:text-xl font-semibold mb-2">Взять из кампании</h3>
            <p className="text-xs md:text-sm text-gray-500 mb-4">
              Выберите кампанию — тема и текст подтянутся в шаблон
            </p>

            <div className="space-y-2 max-h-80 overflow-y-auto">
              {campaigns.length === 0 && (
                <div className="text-sm text-gray-400 text-center py-6">
                  Нет кампаний
                </div>
              )}
              {campaigns.map(c => (
                <div
                  key={c.campaignId}
                  onClick={() => setSelectedCampaignId(c.campaignId)}
                  className={`p-3 rounded-xl cursor-pointer transition border-2 ${
                    selectedCampaignId === c.campaignId
                      ? "bg-blue-50 border-blue-500"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <div className="font-medium text-sm text-gray-800 truncate">
                    #{c.campaignId} — {c.name || "Без названия"}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 truncate">
                    {Array.isArray(c.subject) ? c.subject[0] : c.subject || "(без темы)"}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col md:flex-row md:justify-end gap-2 mt-5">
              <button
                onClick={() => {
                  setShowFromCampaignModal(false);
                  setSelectedCampaignId(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition order-2 md:order-1"
              >
                Отмена
              </button>
              <button
                onClick={handleCreateFromCampaign}
                disabled={!selectedCampaignId}
                className={`px-4 py-2 rounded-xl text-white transition order-1 md:order-2 ${
                  selectedCampaignId
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-gray-300 cursor-not-allowed"
                }`}
              >
                Создать шаблон
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}